import Echo from "laravel-echo";
import Pusher from "pusher-js";

import type { ApiClient } from "../api";
import { postApiV1BroadcastingAuth } from "../api/generated";
import {
  parseNotificationsReadEvent,
  type NotificationsReadEvent,
  type RealtimeConfig,
  type SystemNotification,
} from "../entities/notifications";

type NotificationHandler = (notification: SystemNotification) => void;
type ReadHandler = (event: NotificationsReadEvent) => void;

export class RealtimeManager {
  #echo: Echo<"reverb"> | null = null;

  constructor(private readonly client: ApiClient) {}

  connect(config: RealtimeConfig, onReconnect?: () => void): void {
    if (!config.enabled || config.key === null || config.host === null) return;
    this.disconnect();
    (window as unknown as { Pusher: typeof Pusher }).Pusher = Pusher;
    this.#echo = new Echo<"reverb">({
      broadcaster: "reverb",
      key: config.key,
      wsHost: config.host,
      wsPort: config.port,
      wssPort: config.port,
      forceTLS: config.scheme === "https",
      enabledTransports: ["ws", "wss"],
      authorizer: (channel) => ({
        authorize: (socketId, callback) => {
          void (async () => {
            try {
              const response = await postApiV1BroadcastingAuth(this.client, {
                body: {
                  socket_id: socketId,
                  channel_name: channel.name,
                },
              });
              callback(null, response);
            } catch (error) {
              callback(error as Error, null);
            }
          })();
        },
      }),
    });
    // Echo payload — лишь подсказка. После каждого соединения перечитываем durable API.
    const connection = (this.#echo.connector as unknown as { pusher?: { connection?: { bind: (event: string, listener: () => void) => void } } }).pusher?.connection;
    connection?.bind("connected", () => onReconnect?.());
  }

  /**
   * Изменения внутреннего обсуждения документа.
   *
   * Обсуждение — это чат: сообщение коллеги должно появляться само. Сервер
   * присылает только повод перечитать ленту, сам текст приходит обычным
   * запросом — так видно ровно то, что человеку положено видеть.
   */
  subscribeToDocument(documentId: string, handler: () => void): () => void {
    if (this.#echo === null)
      throw new Error("Realtime connection is not configured.");
    const channelName = `documents.${documentId}`;
    this.#echo.private(channelName).listen(".discussion.changed", handler);

    return () => this.#echo?.leave(channelName);
  }

  /**
   * Личный канал человека: новые уведомления и прочтение на других устройствах.
   *
   * Второе нужно, чтобы отметка «прочитано» на одном устройстве доходила до
   * остальных без перезагрузки. Как и всё в realtime, это лишь подсказка:
   * содержимое списка и счётчика по-прежнему читается из API.
   */
  subscribeToUser(
    userId: string,
    handler: NotificationHandler,
    onRead?: ReadHandler,
  ): () => void {
    if (this.#echo === null)
      throw new Error("Realtime connection is not configured.");
    const channelName = `users.${userId}`;
    const channel = this.#echo.private(channelName).listen(".notification.created", handler);
    if (onRead !== undefined) {
      channel.listen(".notifications.read", (payload: unknown) => onRead(parseNotificationsReadEvent(payload)));
    }

    return () => this.#echo?.leave(channelName);
  }

  disconnect(): void {
    this.#echo?.disconnect();
    this.#echo = null;
  }
}
